import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserModeService {
  private modeSubject = new BehaviorSubject<'customer' | 'provider' | null>(
    null,
  );
  mode$ = this.modeSubject.asObservable();

  private hasProviderSubject = new BehaviorSubject<boolean>(false);
  hasProvider$ = this.hasProviderSubject.asObservable();

  private cookieName =
    window.location.hostname.includes('dev-') ||
    window.location.hostname.startsWith('localhost')
      ? 'Dev_TruckIt_Frontend_UserType'
      : 'Truckit_user_type_prod';

  constructor(
    private authService: AuthService,
    private cookieService: CookieService,
  ) {
    this.init();
  }

  private init() {
    // Set initial mode from cookie immediately (before user data loads)
    // so the UI can render without waiting for the API call.
    const initialMode = this.cookieService.get(this.cookieName) as
      | 'customer'
      | 'provider'
      | '';
    if (initialMode) {
      this.modeSubject.next(initialMode);
    } else {
      this.modeSubject.next('customer');
      this.ensureSingleCookie('customer');
    }

    // When user data arrives, reconcile the mode (only emit if it changes)
    this.authService.user$.subscribe((user) => {
      if (!user) return;

      const hasProvider = !!user.provider_id;
      this.hasProviderSubject.next(hasProvider);

      const storedMode = this.cookieService.get(this.cookieName) as
        | 'customer'
        | 'provider'
        | '';

      const fallbackMode: 'customer' | 'provider' = hasProvider
        ? storedMode || 'provider'
        : 'customer';

      // Only emit if mode actually changed
      if (this.modeSubject.value !== fallbackMode) {
        this.setMode(fallbackMode);
      }
    });
  }

  setMode(mode: 'customer' | 'provider') {
    this.modeSubject.next(mode);
    this.ensureSingleCookie(mode);
  }

  /*
   * Helper to delete potentially duplicate cookies
   * (e.g. at default path) and set one at root path '/'
   */
  private ensureSingleCookie(mode: 'customer' | 'provider') {
    // Delete cookie at current path (implicit) which might be a duplicate
    this.cookieService.delete(this.cookieName);

    // Delete cookie at root path just to be clean before setting
    this.cookieService.delete(this.cookieName, '/');

    // Set canonical cookie at root path
    this.cookieService.set(this.cookieName, mode, 7, '/');
  }

  get currentMode() {
    return this.modeSubject.value;
  }

  get hasProvider() {
    return this.hasProviderSubject.value;
  }
}
